import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TextButton from '@spothero/ui/text-button';
import SpotUtils from 'utils/spot';
import Image from 'common/image/Image';
import withConfig from 'config/withConfig';
import StarRatingWithCategory from 'common/star-rating/StarRatingWithCategory';
import WalkingDistanceDisplay from 'spot-details/map/WalkingDistanceDisplay';

class TransientMonthlyFacilityDetails extends Component {
    static propTypes = {
        spot: PropTypes.object.isRequired,
        isAdmin: PropTypes.bool,
        isMonthly: PropTypes.bool,
        showAdminLink: PropTypes.bool,
        imageWidth: PropTypes.number,
        imageHeight: PropTypes.number,
        staticBaseUrl: PropTypes.string,
        showPrice: PropTypes.bool,
        showImages: PropTypes.bool,
        showFullTitle: PropTypes.bool,
        isLowInventoryEnabled: PropTypes.bool,
    };
    static defaultProps = {
        imageWidth: 75,
        imageHeight: 75,
        showPrice: true,
        showImages: true,
        showFullTitle: false,
    };

    constructor(props) {
        super(props);

        const {
            imageWidth,
            imageHeight,
            spot: {facility, title},
            staticBaseUrl,
        } = props;
        const firstImage = !isUndefined(facility.images[0])
            ? facility.images[0]
            : null;

        this._imgProps = {
            width: imageWidth,
            height: imageHeight,
            itemProp: 'image',
            alt: `Photo of ${title}`,
            lazyLoad: true,
            ...(firstImage
                ? {cloudinaryTemplate: firstImage.url_template}
                : {src: `${staticBaseUrl}img/missing-image.png`}),
        };
    }

    _renderPrice = () => {
        const {
            spot: {
                selectedRate: {
                    display_price: displayPrice,
                    currency_type: currencyType,
                },
            },
        } = this.props;
        const displayPriceFormatted = SpotUtils.formatRatePrice({
            price: displayPrice,
            currencyType,
        });

        return (
            <div
                className="price"
                dangerouslySetInnerHTML={{__html: displayPriceFormatted}}
            />
        );
    };

    _renderSpotSchema = () => {
        const {
            spot: {
                facility: {
                    facility_url: spotUrl,
                    street_address: address,
                    city,
                    state,
                },
                latitude,
                longitude,
            },
        } = this.props;

        return (
            <>
                <span
                    itemProp="address"
                    itemScope
                    itemType="http://schema.org/PostalAddress"
                >
                    <meta itemProp="streetAddress" content={address} />
                    <meta itemProp="addressLocality" content={city} />
                    <meta itemProp="addressRegion" content={state} />
                </span>
                <span
                    itemProp="geo"
                    itemScope
                    itemType="http://schema.org/GeoCoordinates"
                >
                    <meta itemProp="latitude" content={latitude} />
                    <meta itemProp="longitude" content={longitude} />
                </span>
                <span
                    itemProp="containedInPlace"
                    itemScope
                    itemType="http://schema.org/City"
                >
                    <meta itemProp="name" content={city} />
                    <meta
                        itemProp="url"
                        content={spotUrl.split('-parking')[0]}
                    />
                </span>
            </>
        );
    };

    render() {
        const {
            isAdmin,
            showAdminLink,
            spot: {
                spotId,
                title,
                selectedRate,
                distance,
                distanceInMiles,
                walkingDuration,
                facility: {
                    rating_info: {
                        number_of_ratings: numberOfReviews,
                        star_rating: starRating,
                    },
                },
                available_transient_inventory: transientInventory,
                available_monthly_inventory: monthlyInventory,
            },
            isMonthly,
            showFullTitle,
            showPrice,
            showImages,
            isLowInventoryEnabled,
        } = this.props;
        const {unavailable_reason: unavailableReason} = selectedRate;
        const classes = classNames('TransientMonthlyFacilityDetails', {
            'TransientMonthlyFacilityDetails-hide-images': !showImages,
        });
        const titleClasses = classNames(
            'TransientMonthlyFacilityDetails-title',
            {'show-full-title': showFullTitle},
            {'hide-price': !showPrice}
        );
        const inventory =
            isMonthly && monthlyInventory
                ? monthlyInventory.default_rate
                : transientInventory;
        const inventoryLimit = isMonthly ? 2 : 3;

        return (
            <div className={classes}>
                {showImages && (
                    <div className="images">
                        <Image {...this._imgProps} />
                    </div>
                )}
                <div className="detail">
                    <h2 className="heading-md">
                        <span className={titleClasses} itemProp="name">
                            {title}
                        </span>
                        {isAdmin && showAdminLink && (
                            <TextButton href={`/admin/spots/${spotId}`}>
                                Facility Admin
                            </TextButton>
                        )}
                    </h2>
                    <StarRatingWithCategory
                        numberOfReviews={numberOfReviews}
                        starRating={starRating}
                    />
                    <WalkingDistanceDisplay
                        distance={distance}
                        distanceInMiles={distanceInMiles}
                        walkingDuration={walkingDuration}
                    />
                    {isLowInventoryEnabled &&
                        inventory > 0 &&
                        inventory <= inventoryLimit && (
                            <p className="TransientMonthlyFacilityDetails-inventory">
                                Only {inventory}{' '}
                                {`spot${inventory > 1 ? 's' : ''}`} left!
                            </p>
                        )}
                    {this._renderSpotSchema()}
                    {isAdmin && unavailableReason && (
                        <span className="not-available-reason text-light">
                            <sup>*</sup> {unavailableReason}
                        </span>
                    )}
                </div>
                {showPrice && (
                    <div className="TransientMonthlyFacilityDetails-price-container">
                        {this._renderPrice()}
                        <span>{isMonthly ? '/month' : 'total'}</span>
                    </div>
                )}
            </div>
        );
    }
}

export default withConfig(['staticBaseUrl'])(TransientMonthlyFacilityDetails);
